import { useClientPortalAPI, useQueryParams } from "@certn/api";
import { Card, MediaQuery, Divider, mq } from "@certn/cascadia-react";
import { useTimeTracking } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";

import { BundleSection } from "./components/BundleSection";
import { CheckSection } from "./components/CheckSection";
import { ContentHeader } from "./components/ContentHeader";
import { CollapsibleSection } from "../../../components/collapsible-section/CollapsibleSection";
import { OrderSummary } from "../../../components/order-summary/OrderSummary";
import { LAST_SELECTED_CLIENT_GROUP_KEY } from "../../../utils/check-utils";
import { useClientAccount } from "../../../hooks/useClientAccount";
import { OrderContext } from "../OrderContext";
import { OrderStepHeader } from "../components/OrderStepHeader";
import { OrderStepLayout } from "../components/OrderStepLayout";
import { ORDER_TAB_TYPES } from "../../../constants";

export function CheckSelectionStep() {
  const { t } = useTranslation();
  const { order, goToNextStep } = useContext(OrderContext);
  const { params, setParam } = useQueryParams();
  const { enableCentricQuickscreen } = useFlags();
  const { client } = useClientAccount();
  const quickscreenEnabled =
    !!enableCentricQuickscreen && !!client?.quickscreen_enabled;

  const selectedTab: (typeof ORDER_TAB_TYPES)[keyof typeof ORDER_TAB_TYPES] =
    params.tab === ORDER_TAB_TYPES.QUICKSCREEN && quickscreenEnabled
      ? ORDER_TAB_TYPES.QUICKSCREEN
      : ORDER_TAB_TYPES.APPLICANT_SUBMISSION;
  const setSelectedTab = (
    tab: (typeof ORDER_TAB_TYPES)[keyof typeof ORDER_TAB_TYPES]
  ) => setParam("tab", tab, false);

  const customerGroupId = params.customer_group_id
    ? String(params.customer_group_id)
    : null;
  const { data: customerGroups = [] } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/customer-groups/`
  );
  const { startTimeTracker, stopTimeTracker } = useTimeTracking();
  const [selectedGroup, setSelectedGroup] = useState<string | null>("");
  const isNextDisabled =
    order.checks.length === 0 && order.bundles.length === 0;

  useEffect(() => {
    if (selectedGroup === null && customerGroups.length > 0) {
      setSelectedGroup(customerGroups[0].id);
    }
  }, [customerGroups, selectedGroup]);

  useEffect(() => {
    if (
      order.group ||
      customerGroupId ||
      localStorage.getItem(LAST_SELECTED_CLIENT_GROUP_KEY) ||
      (customerGroups.length > 0 ? customerGroups[0].id : null)
    ) {
      setSelectedGroup(
        order.group ||
          localStorage.getItem(LAST_SELECTED_CLIENT_GROUP_KEY) ||
          (customerGroups.length > 0 ? customerGroups[0].id : null)
      );
    }
  }, [customerGroupId, customerGroups, order.group, startTimeTracker]);

  useEffect(() => {
    startTimeTracker("order--check-selection-step");
  }, [startTimeTracker]);

  return (
    <>
      <MediaQuery lg xl>
        <OrderStepLayout
          onComplete={() => {
            stopTimeTracker("order--check-selection-step");
            goToNextStep();
          }}
          disableCompletion={isNextDisabled}
          backgroundColor="surface-sunken"
          contentHeader={
            <ContentHeader
              customerGroups={customerGroups}
              selectedGroup={selectedGroup}
              setSelectedGroup={setSelectedGroup}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              showQuickscreen={quickscreenEnabled}
            />
          }
          rightSideBar={
            <OrderSummary
              checks={order.checks}
              bundles={order.bundles}
              title={t("Order summary")}
            />
          }
        >
          <Card className={styles.mainContent}>
            <BundleSection
              group={selectedGroup}
              quickscreenOnly={selectedTab === ORDER_TAB_TYPES.QUICKSCREEN}
            />
            <CheckSection
              group={selectedGroup}
              quickscreenOnly={selectedTab === ORDER_TAB_TYPES.QUICKSCREEN}
            />
          </Card>
        </OrderStepLayout>
      </MediaQuery>
      <MediaQuery sm md>
        <OrderStepHeader
          onComplete={() => {
            goToNextStep();
          }}
          isDisabled={isNextDisabled}
        />
        <Divider />
        <div className={styles.mobileLayout}>
          <div className={styles.mobileCard}>
            <ContentHeader
              customerGroups={customerGroups}
              selectedGroup={selectedGroup}
              setSelectedGroup={setSelectedGroup}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              showQuickscreen={quickscreenEnabled}
            />
          </div>
          <div
            className={css(
              styles.mobileCard,
              css({
                paddingLeft: "var(--space-1)",
                paddingRight: "var(--space-1)",
              })
            )}
          >
            <CollapsibleSection
              data={
                <OrderSummary checks={order.checks} bundles={order.bundles} />
              }
              title={t("Order summary")}
              testID="order-summary-toggle"
            />
          </div>
          <div className={css(styles.mobileCard, styles.mobileContent)}>
            <BundleSection
              group={selectedGroup}
              quickscreenOnly={selectedTab === ORDER_TAB_TYPES.QUICKSCREEN}
            />
            <CheckSection
              group={selectedGroup}
              quickscreenOnly={selectedTab === ORDER_TAB_TYPES.QUICKSCREEN}
            />
          </div>
        </div>
      </MediaQuery>
    </>
  );
}

const styles = {
  mainContent: css(
    {
      display: "flex",
      flexDirection: "column",
      padding: "var(--space-5) var(--space-5) var(--space-10)",
      gap: "var(--space-10)",
      maxWidth: 976,
      width: "100%",
    },
    mq({
      maxWidth: ["none", 976],
    })
  ),
  mobileLayout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
    backgroundColor: "var(--surface-sunken)",
    paddingBottom: "var(--space-5)",
  }),
  mobileCard: css({
    backgroundColor: "var(--surface-base)",
    padding: "var(--space-5) var(--space-4)",
  }),
  mobileContent: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-10)",
  }),
};
